import axios from 'axios';

function getApiBaseUrl(): string {
  if (typeof window !== 'undefined' && /yuvanapi\.cloud$/i.test(window.location.hostname)) {
    return 'https://api.yuvanapi.cloud/api/v1';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
}

const API_BASE_URL = getApiBaseUrl();
// PoolPay v2 uses /api/v2/... not /api/v1/v2/...
const API_BASE_NO_V1 = API_BASE_URL.replace(/\/v1\/?$/, '');

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,   
  headers: {
    'Content-Type': 'application/json',   
  },
}); 

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // PoolPay v2 routes: use /api base (not /api/v1) so /v2/poolpay becomes /api/v2/poolpay
    if (config.url?.startsWith('/v2/poolpay')) {
      config.baseURL = API_BASE_NO_V1;
    }
    // Log PoolPay payout requests to console for debugging
    const url = `${config.baseURL}${config.url}`;
    if (url.includes('poolpay')) {
      console.log('[PoolPay Request]', {
        method: config.method?.toUpperCase(),
        url,
        params: config.params,
        data: config.data,
      });
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Log PoolPay payout responses to console for debugging
    if (response.config?.url?.includes('poolpay')) {
      console.log('[PoolPay Response]', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    // Log PoolPay payout error responses to console for debugging
    if (error.config?.url?.includes('poolpay')) {
      console.log('[PoolPay Error]', {
        status: error.response?.status,
        url: error.config?.url,
        data: error.response?.data,
        message: error.message,
      });
    }
    if (error.response?.status === 401) {
      // Don't redirect for public endpoints (avoids redirect loop on login page)
      const isPublicRequest = error.config?.url?.includes('maintenance-status');
      if (!isPublicRequest) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data: { name: string; email: string; password: string; phone?: string }) =>
    api.post('/auth/register', data),
  
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  sendOtp: (mobile: string, password?: string) => {
    return api.post('/auth/send-otp', { mobile, ...(password ? { password } : {}) });
  },
  
  verifyOtp: (mobile: string, otp: string) => {
    // Use backend endpoint which will forward to external OTP service
    return api.post('/auth/verify-otp', { mobile, otp });
  },
  
  verifyOtpAfterLogin: (otp: string) => {
    // Use backend endpoint which requires authentication
    return api.post('/auth/verify-otp-after-login', { otp });
  },

  sendMobileChangeOtp: (mobile: string) =>
    api.post('/auth/send-mobile-change-otp', { mobile }),

  changeMobileWithOtp: (data: { old_mobile: string; otp: string; new_mobile: string }) =>
    api.post('/auth/change-mobile-with-otp', data),

  forgotPassword: (data: { email?: string; mobile?: string }) =>
    api.post('/auth/forgot-password', data),

  /** Send OTP to mobile for forgot-password flow (no password required) */
  sendOtpForForgotPassword: (mobile: string) =>
    api.post('/auth/send-otp', { mobile, context: 'password_reset' }),

  /** Verify OTP only (before showing new password fields). Fails on wrong OTP. */
  verifyOtpForPasswordReset: (mobile: string, otp: string) =>
    api.post('/auth/verify-otp-for-password-reset', { mobile, otp }),

  /** Reset password after OTP verification (forgot password) */
  resetPasswordWithOtp: (data: { mobile: string; otp: string; new_password: string }) =>
    api.post('/auth/reset-password-with-otp', data),

  requestTpin: (data: { mobile: string; password: string }) =>
    api.post('/auth/request-tpin', data),
  
  getProfile: () => api.get('/auth/profile'),
  
  updateProfile: (data: any) => api.put('/auth/profile', data),

  getTheme: () => api.get('/auth/theme'),
  updateTheme: (data: { sidebarColor?: string; headerColor?: string; contentColor?: string; sectionColor?: string; tableColor?: string }) =>
    api.put('/auth/theme', data),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  
  logout: () => api.post('/auth/logout'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  
  getAdminStats: () => api.get('/dashboard/admin/stats'),
  
  getUsers: (params?: any) => api.get('/dashboard/admin/users', { params }),
  
  getRecentActivity: (limit?: number) =>
    api.get('/dashboard/admin/recent-activity', { params: { limit } }),
  
  getPayoutStats: (period?: number) =>
    api.get('/dashboard/payouts/stats', { params: { period } }),
  
  getRecentPayouts: (limit?: number) =>
    api.get('/dashboard/payouts/recent', { params: { limit } }),
  
  getMyActivity: () => api.get('/dashboard/my-activity'),
};

// Payout API
export const payoutAPI = {
  initiate: (data: any) => api.post('/viyon/payouts/initiate', data),
  
  getStatus: (merchantRef: string) =>
    api.get(`/viyon/payouts/status/${merchantRef}`),
  
  getAll: (params?: any) => api.get('/viyon/payouts', { params }),
  
  getById: (id: number) => api.get(`/viyon/payouts/${id}`),
  
  getStats: () => api.get('/viyon/payouts/stats'),
};

// Account/Wallet actions for merchants
export const accountAPI = {
  // Generic withdrawal endpoint (backend should implement)
  withdraw: (data: { accountNumber: string; ifscCode: string; amount: number; name?: string }) =>
    api.post('/merchants/account-withdraw', data),
  bankNameFromIFSC: (ifsc: string) => api.get(`/viyon/payouts/ifsc/${ifsc}`),
  // Sync API keys from manager (fetches latest client_id, client_secret, api_key from manager's profile)
  syncApiKeysFromManager: () => api.post('/merchants/sync-api-keys-from-manager'),
};

// Public API (no authentication required)
export const publicAPI = {
  getMaintenanceStatus: () => api.get('/maintenance-status'),
};

// Admin API
export const adminAPI = {
  // Statistics & Dashboard
  getStats: () => api.get('/admin/stats'),
  getDashboardOverview: () => api.get('/admin/dashboard-overview'),
  
  getReports: (period?: number) => api.get('/admin/reports', { params: { period } }),
  
  getActivityLogs: (params?: any) => api.get('/admin/activity-logs', { params }),
  
  // User Management
  getAllUsers: (params?: any) => api.get('/admin/users', { params }),
  
  getUserById: (userId: number) => api.get(`/admin/users/${userId}`),
  
  createUser: (data: any) => api.post('/admin/users', data),
  
  updateUser: (userId: number, data: any) => api.put(`/admin/users/${userId}`, data),
  
  deleteUser: (userId: number) => api.delete(`/admin/users/${userId}`),
  
  toggleUserStatus: (userId: number, is_active: boolean) =>
    api.patch(`/admin/users/${userId}/toggle-status`, { is_active }),
  
  resetUserPassword: (userId: number, new_password: string) =>
    api.post(`/admin/users/${userId}/reset-password`, { new_password }),
  
  getUserRoles: () => api.get('/admin/roles'),
  
  // Transaction Management
  getAllTransactions: (params?: any) => api.get('/admin/transactions', { params }),
  
  getTransactions: (params?: any) => api.get('/admin/transactions', { params }),
  
  // Payout Management
  getAllPayouts: (params?: any) => api.get('/admin/payouts', { params }),
  
  // System Settings
  getSystemSettings: () => api.get('/admin/settings'),
  
  updateSystemSettings: (settings: any) => api.put('/admin/settings', settings),
  
  // Emergency Actions
  emergencyAction: (action: string) => api.post('/admin/emergency-action', { action }),
  
  // Admin Wallet
  getAdminWallet: () => api.get('/admin/wallet'),
  
  transferToUser: (data: { toUserId: number; amount: number; description: string }) =>
    api.post('/admin/wallet/transfer', data),
  
  getUsersForTransfer: () => api.get('/admin/wallet/users'),
  
  generateCoins: () => api.post('/admin/wallet/generate-coins'),
  
  getUsers: () => api.get('/admin/users'),
};

export default api;




